import { useEffect, useState } from 'react';
import { settingsService } from '@/services/settingsService';
import { Button } from '@/components/ui/button';
import { ArrowDown } from 'lucide-react';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const HeroSection = () => {
  const [targetDate, setTargetDate] = useState<Date>(new Date('2026-04-15T23:59:59'));
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Fetch deadline from database
  useEffect(() => {
    const fetchDeadline = async () => {
      const deadline = await settingsService.getSubmissionDeadline();
      setTargetDate(deadline);
      console.log('📅 Loaded deadline:', deadline);
    };
    fetchDeadline();
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const scrollToForm = () => {
    document.getElementById('submission-form')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[128px] animate-pulse delay-1000" />
        <div className="absolute bottom-1/4 left-1/2 w-96 h-96 bg-accent/20 rounded-full blur-[128px] animate-pulse delay-2000" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 text-center">
        {/* Main headline with gradient */}
        <h1 className="text-6xl md:text-8xl font-bold mb-8 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-fade-in leading-tight">
          Dream SaaS Empire
        </h1>

        {/* Subheadline */}
        <div className="space-y-4 mb-12 max-w-3xl mx-auto">
          <p className="text-2xl md:text-3xl font-semibold text-foreground/90">
            Unlimited budget. Unlimited team. Zero excuses.
          </p>
          <p className="text-xl md:text-2xl text-foreground/70 leading-relaxed">
            What game-changing SaaS would YOU build if money and manpower were infinite?
          </p>
          <p className="text-lg md:text-xl text-accent font-medium">
            Submit your wildest idea below. The best ones win free Layout.dev credits + massive shoutouts.
          </p>
        </div>

        {/* CTA Button */}
        <Button
          onClick={scrollToForm}
          size="lg"
          className="group relative mb-8 px-12 py-8 text-xl font-bold bg-gradient-to-r from-primary via-secondary to-accent hover:shadow-[0_0_40px_rgba(96,165,250,0.6)] transition-all duration-300 hover:scale-105"
        >
          <span className="relative z-10 flex items-center gap-3">
            Submit Your Dream Idea Now
            <ArrowDown className="w-6 h-6 animate-bounce" />
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-secondary/80 to-accent/80 rounded-lg blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
        </Button>

        {/* Countdown Timer */}
        <div className="inline-flex items-center gap-4 px-8 py-4 rounded-full bg-card/50 backdrop-blur-sm border border-border/50">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{timeLeft.days}</div>
              <div className="text-xs text-muted-foreground uppercase">Days</div>
            </div>
            <div className="text-2xl text-muted-foreground">:</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-secondary">{timeLeft.hours}</div>
              <div className="text-xs text-muted-foreground uppercase">Hours</div>
            </div>
            <div className="text-2xl text-muted-foreground">:</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent">{timeLeft.minutes}</div>
              <div className="text-xs text-muted-foreground uppercase">Mins</div>
            </div>
            <div className="text-2xl text-muted-foreground">:</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground/70">{timeLeft.seconds}</div>
              <div className="text-xs text-muted-foreground uppercase">Secs</div>
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          Submissions close {targetDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-accent/50 flex items-start justify-center p-2">
          <div className="w-1.5 h-2 bg-accent rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};
